import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bot, EyeOff, Sparkles, BookOpen, X } from 'lucide-react';

interface GettingStartedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GettingStartedModal({ isOpen, onClose }: GettingStartedModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-zinc-900/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
          className="relative w-full max-w-lg bg-zinc-50 dark:bg-[#121318] border border-zinc-200 dark:border-[#2e3138] rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 text-center space-y-3 bg-gradient-to-b from-green-500/10 to-transparent">
            <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-green-500/20 mb-2">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-bold font-heading tracking-tight text-zinc-900 dark:text-white">
              Welcome to UndetectableCoursework
            </h2>
            <p className="text-[14px] text-zinc-500 dark:text-zinc-400 max-w-md mx-auto leading-relaxed">
              Your ultimate academic toolkit to generate, humanize, and check coursework effortlessly.
            </p>
          </div>

          {/* Features */}
          <div className="p-6 space-y-5 bg-white dark:bg-[#1a1c23]">
            <div className="flex gap-4">
               <div className="shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20">
                 <EyeOff className="w-5 h-5 text-green-600 dark:text-[#00ff00]" />
               </div>
               <div>
                  <h3 className="font-bold text-zinc-900 dark:text-white mb-1">Stealth Mode 0% Detection</h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">Activate stealth mode to reconstruct your text with high cognitive burstiness, guaranteeing passing scores on Turnitin and other detectors.</p>
               </div>
            </div>

            <div className="flex gap-4">
               <div className="shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                 <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
               </div>
               <div>
                  <h3 className="font-bold text-zinc-900 dark:text-white mb-1">Smart Humanizer Focus</h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">Instantly convert generic AI output into rich, personalized academic prose matching your natural writing style.</p>
               </div>
            </div>

            <div className="flex gap-4">
               <div className="shrink-0 w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                 <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
               </div>
               <div>
                  <h3 className="font-bold text-zinc-900 dark:text-white mb-1">Citation Auto-Generator</h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">Seamlessly integrate accurate, formatted references directly into your coursework based on the raw context provided.</p>
               </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 bg-zinc-50 dark:bg-[#121318] border-t border-zinc-200 dark:border-[#2e3138] flex justify-end">
            <button 
              onClick={onClose}
              className="w-full sm:w-auto bg-green-600 dark:bg-[#00ff00] text-white dark:text-black hover:bg-green-700 dark:hover:bg-[#00cc00] font-bold py-2.5 px-6 rounded-lg transition-colors shadow-lg shadow-green-500/20"
            >
              Start Creating Now
            </button>
          </div>
          
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
